import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import useSWRImmutable from 'swr/immutable';

import PageNotFoundBanner from './PageNotFoundBanner';
import { CarouselProducts } from 'components/Common/Carousel/CarouselProducts';
import { ErrorBoundary } from 'components/Common/Errors/ErrorBoundary/ErrorBoundary';
import useGtmPageView from 'hooks/gtm/useGtmPageView';
import useEnv from 'hooks/useEnv';
import { getSlideType } from 'services/carousel';
import { fetcher } from 'services/fetcher';
import {
  getFormattedFlowAttributeDefinition,
  getFormattedProductDetailToCarouselProduct,
} from 'services/formatter';
import { getMerchActionProducts } from 'services/merchandising';
import { DESKTOP_MIN, MOBILE_MIN, TABLET_PORTRAIT_MIN } from 'settings/device';
import { GTM_PAGE_ERROR } from 'settings/gtm/gtmPageView';
import {
  MAX_PRODUCTS_SearchNoResultDecouvrezAussiProduit,
  MAX_PRODUCTS_SearchNoResultVousAimerezProduit,
} from 'settings/merchandising';
import { FormattedFlowAttributeType } from 'types/formatter';
import { ProductDetail } from 'types/Controls/ProductSheet';
import { SearchNoResult } from 'types/SearchNoResult';

import styles from './PageNotFound.module.scss';

export default function PageNotFound({
  status,
}: {
  status?: string;
}): JSX.Element {
  const { t } = useTranslation();
  const { datetimeApp, urlApiCatalog } = useEnv();
  const { data } = useSWRImmutable<SearchNoResult>(
    `${urlApiCatalog}/json/Custom/SearchNoResult.cxml?${datetimeApp}`,
    fetcher
  );

  useGtmPageView(GTM_PAGE_ERROR, '', {
    error: status ?? '404',
    isLoaded: true,
  });

  const attributes = useMemo(() => {
    return getFormattedFlowAttributeDefinition(
      data?.Header?.Controls[1]?.Attributes
    );
  }, [data?.Header?.Controls[1]?.Attributes]);

  const shoppingIdeaProductDetails = useMemo(() => {
    return getMerchActionProducts(
      data?.Zones[1]?.Controls[0]?.Actions,
      MAX_PRODUCTS_SearchNoResultVousAimerezProduit
    );
  }, [
    data?.Zones[1]?.Controls[0]?.Actions,
    MAX_PRODUCTS_SearchNoResultVousAimerezProduit,
  ]);

  const alsoDiscoverProductDetails = useMemo(() => {
    return getMerchActionProducts(
      data?.Zones[2]?.Controls[0]?.Actions,
      MAX_PRODUCTS_SearchNoResultDecouvrezAussiProduit
    );
  }, [
    data?.Zones[2]?.Controls[0]?.Actions,
    MAX_PRODUCTS_SearchNoResultDecouvrezAussiProduit,
  ]);

  return (
    <>
      <Helmet
        meta={[
          {
            name: 'prerender-status-code',
            content: status ?? '404',
          },
        ]}
      />
      <PageNotFoundBanner />
      {shoppingIdeaProductDetails.length > 0 && (
        <ErrorBoundary fallback={<></>}>
          <RenderCarousel
            title={t('LABEL_SHOPPING_IDEA')}
            productDetails={shoppingIdeaProductDetails}
            attributes={attributes}
          />
        </ErrorBoundary>
      )}
      {alsoDiscoverProductDetails.length > 0 && (
        <ErrorBoundary fallback={<></>}>
          <RenderCarousel
            title={t('LABEL_ALSO_DISCOVER')}
            productDetails={alsoDiscoverProductDetails}
            attributes={attributes}
          />
        </ErrorBoundary>
      )}
    </>
  );
}

function RenderCarousel({
  title,
  productDetails,
  attributes,
}: {
  title: string;
  productDetails: ProductDetail[];
  attributes: FormattedFlowAttributeType;
}) {
  const products = useMemo(() => {
    return getFormattedProductDetailToCarouselProduct(
      productDetails,
      attributes
    );
  }, [productDetails, attributes]);

  return (
    <CarouselProducts
      title={title}
      products={products}
      classes={{
        carousel: { wrapper: styles.carousel, title: styles.carouselTitle },
      }}
      splideOptions={{
        mediaQuery: 'min',
        perPage: 2,
        type: getSlideType(2, products.length),
        breakpoints: {
          [MOBILE_MIN]: {
            perPage: 3,
          },
          [TABLET_PORTRAIT_MIN]: {
            perPage: 4,
            type: getSlideType(4, products.length),
          },
          [DESKTOP_MIN]: {
            gap: '20px',
          },
        },
      }}
    />
  );
}
